import { designVariables } from "@studio3t/design-variables";
import {
  BadgeConfirmationSubtle,
  BadgeErrorSubtle,
  BadgeSecondarySubtle,
  Icon,
  IconSVG,
} from "@studio3t/generic-components";
import styled, { type DefaultTheme, useTheme } from "styled-components";
import type { NormalizedJobState } from "@/pages/pipelineHelpers";

const BADGE = designVariables.ui.badge;
const SINGLE_LINE = designVariables.type.singleLine;

// Settled states (the ones that render as a colored badge); the transitional
// states are handled separately as text.
type SettledJobState = "Running" | "Stopped" | "Failed";

interface StatusBadgeStyle {
  Badge: typeof BadgeSecondarySubtle;
  icon: Icon;
  color: (theme: DefaultTheme) => string;
}

const STATUS_BADGES: Record<SettledJobState, StatusBadgeStyle> = {
  Running: {
    Badge: BadgeConfirmationSubtle,
    icon: Icon.Run,
    color: (t) => t.badgeConfirmSubtleColor,
  },
  Failed: {
    Badge: BadgeErrorSubtle,
    icon: Icon.Error,
    color: (t) => t.badgeErrorSubtleColor,
  },
  Stopped: {
    Badge: BadgeSecondarySubtle,
    icon: Icon.Stop,
    color: (t) => t.badgeSecondarySubtleText,
  },
};

// Transitional states (Starting/Stopping) render as muted italic text
// ("Starting…"/"Stopping…") rather than a badge, per Figma node 13532-56320:
// 12px (single-line/small) italic, semantic/textlight.
const TransitionalText = styled.span`
  font-size: ${SINGLE_LINE.small.regular.fontSize}px;
  font-style: italic;
  line-height: 1.2;
  white-space: nowrap;
  color: ${(props) => props.theme.semanticTextLight};
`;

export function JobStatusBadge({ state }: { state: NormalizedJobState }) {
  const theme = useTheme();
  if (state === "Starting" || state === "Stopping") {
    return <TransitionalText>{state}…</TransitionalText>;
  }
  const { Badge, icon, color } = STATUS_BADGES[state];
  return (
    <Badge>
      <IconSVG
        icon={icon}
        size={BADGE.icon}
        colors={{ default: color(theme) }}
      />
      {/* Figma shows settled status labels in lowercase ("running", "stopped", ...). */}
      {state.toLowerCase()}
    </Badge>
  );
}
